import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase, clearStaleAuth, handleAuthRefreshError, safeGetSession } from '@/lib/supabase';
import { clearStalePortalProfileCache, fetchPortalProfile } from '@/lib/portalProfile';
import type { User } from '@supabase/supabase-js';

export interface AuthProfile {
  id: string;
  full_name: string | null;
  email: string;
  user_type: 'management' | 'subcontractor' | 'client';
  company_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AuthState {
  user: User | null;
  profile: AuthProfile | null;
  loading: boolean;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const profileFetchGenRef = useRef(0);

  const fetchProfile = useCallback(async (userId: string, userEmail?: string | null) => {
    const gen = ++profileFetchGenRef.current;
    const startedAt = performance.now();

    try {
      clearStalePortalProfileCache();
      console.log('[useAuth] Fetching portal profile for user_id:', userId, 'email:', userEmail);

      const { profile: portalProfile, source } = await fetchPortalProfile(userId, userEmail);

      console.log(`[useAuth] ⏱ profile fetch: ${(performance.now() - startedAt).toFixed(0)}ms`, {
        source,
        full_name: portalProfile?.full_name ?? null,
      });

      if (gen !== profileFetchGenRef.current) return;

      setProfile(portalProfile as AuthProfile | null);
      setLoading(false);
    } catch (err) {
      console.log('[useAuth] Profile fetch threw:', err);
      if (gen !== profileFetchGenRef.current) return;
      const cleared = await handleAuthRefreshError(err);
      if (cleared) {
        setUser(null);
        setProfile(null);
      } else {
        setProfile(null);
      }
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearStaleAuth();
    clearStalePortalProfileCache();

    async function initSession() {
      const startedAt = performance.now();
      try {
        const { data: { session }, error } = await safeGetSession();
        console.log(`[useAuth] ⏱ getSession: ${(performance.now() - startedAt).toFixed(0)}ms`, {
          hasSession: !!session,
          userId: session?.user?.id,
          error: error?.message,
        });
        if (error) {
          const cleared = await handleAuthRefreshError(error);
          if (cleared) {
            console.log('[useAuth] Cleared invalid session after getSession error');
            setUser(null);
            setProfile(null);
            setLoading(false);
            return;
          }
          console.log('[useAuth] getSession error, clearing state');
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setUser(session?.user ?? null);
        if (session?.user) {
          setProfile(null);
          await fetchProfile(session.user.id, session.user.email);
        } else {
          setProfile(null);
          setLoading(false);
        }
      } catch (err) {
        console.log('[useAuth] getSession threw:', err);
        const cleared = await handleAuthRefreshError(err);
        if (cleared) {
          setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    }

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[useAuth] onAuthStateChange event:', event, 'userId:', session?.user?.id);
      if (event === 'SIGNED_OUT') {
        profileFetchGenRef.current += 1;
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        setProfile(null);
        setLoading(true);
        void fetchProfile(session.user.id, session.user.email);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  return { user, profile, loading };
}
